import { useSurveyStore } from '@/stores/useSurveyStore'
import styles from './Survey.module.css'
import { useMemo } from 'react'
import { SurveyAction, SurveyActionTypes, SurveyContent, SurveyStepTypes } from '@/interfaces/survey_types'
import FormSurvey from './SurveySteps/FormSurvey/FormSurvey'
import SelectIconSurvey from './SurveySteps/SelectIconSurvey/SelectIconSurvey'
import SelectButtonSurvey from './SurveySteps/SelectButtonSurvey/SelectButtonSurvey'
import MessageIconSurvey from './SurveySteps/MessageIconSurvey/MessageIconSurvey'
import IconCross from '@/assets/img/Icon_Cross.svg'
import { validateConditionsSurvey } from '@/utils/validators'
import { useModalStore } from '@/stores/useModalStore'
import IconButton, { IconButtonKind, IconButtonSize } from '../UI/Buttons/IconButton/IconButton'

interface SurveyProps extends React.ComponentProps<'div'> {
  submit: () => void
}

export default function Survey ({ submit, ...rest }: SurveyProps) {
  const { survey, fields, currentStep, setStep, previousStep, setField, reset } = useSurveyStore()
  const { closeModal } = useModalStore()

  const totalSteps = useMemo(() => {
    return Object.keys(survey?.steps ?? {}).length
  }, [survey])

  /**
   * Executes a list of survey actions based on their type
   * 
   * @param actions List of actions to process
   * @returns void
   */
  function handleActions (actions: SurveyAction[] | null | undefined) {
    if (!actions) return

    const isSubmitBtn = actions.some(elem => elem.action === SurveyActionTypes.SUBMIT)

    actions.forEach(action => {
      switch (action.action) {
        case SurveyActionTypes.EXIT:
          if(!isSubmitBtn) {
            reset()
            closeModal()
          }
          break
        case SurveyActionTypes.SUBMIT:
          submit()
          break
        case SurveyActionTypes.FIELD_SET:
          const isValid = validateConditionsSurvey(action.if, fields)
          if (isValid) setField(action.field, action.value)
          break
        case SurveyActionTypes.GO_TO:
          setStep(action.to)
          break
        case SurveyActionTypes.HISTORY_BACK:
          if (history.length <= 1) closeModal()
          else previousStep()
          break
      }
    })
  }

  /**
   * Renders the current survey step based on its index.
   *
   * @param step The key of the current step
   * @returns The React node for the current survey step, or null if it cannot be rendered.
   */
  function renderCurrentStepSurvey (survey: SurveyContent, step: string) {
    if (!survey) return null

    const stepContent = survey.steps[step]
    
    switch (stepContent.type) {
      case SurveyStepTypes.FORM:
        return <FormSurvey submit={submit} />
      case SurveyStepTypes.SELECT_ICON:
        return <SelectIconSurvey submit={submit} />
      case SurveyStepTypes.SELECT_BUTTON:
        return <SelectButtonSurvey submit={submit} />
      case SurveyStepTypes.MESSAGE_ICON:
        return <MessageIconSurvey submit={submit} />
      default:
        return null
    }
  }

  if (!survey) return null

  return (
    <div className={styles.survey} {...rest}>
      {survey.steps[currentStep]['close-actions'] &&
      <div className={styles.closeBtnWrapper}>
        <IconButton
          kind={IconButtonKind.EMPTY}
          size={IconButtonSize.S}
          icon={IconCross}
          className={styles.closeBtn}
          onClick={() => handleActions(survey.steps[currentStep]['close-actions'])}
        />
      </div>
      }
      {survey && totalSteps > 0 && renderCurrentStepSurvey(survey, currentStep)}
    </div> 
  )
}